import type { LocalTimeWindow } from "../types";

type HourRange = { startHour: number; endHour: number };

const PRESETS: Record<Exclude<LocalTimeWindow, "custom">, HourRange> = {
  morning: { startHour: 6, endHour: 12 },
  afternoon: { startHour: 12, endHour: 18 },
  night: { startHour: 18, endHour: 6 },
};

const isHourValid = (hour: number | undefined): hour is number =>
  typeof hour === "number" && Number.isFinite(hour) && hour >= 0 && hour <= 23;

function resolveRange(
  window: LocalTimeWindow | undefined,
  customStartHour: number | undefined,
  customEndHour: number | undefined
): HourRange | null {
  if (!window) return null;
  if (window === "custom") {
    if (!isHourValid(customStartHour) || !isHourValid(customEndHour)) return null;
    return { startHour: customStartHour, endHour: customEndHour };
  }
  return PRESETS[window] ?? null;
}

export function isWithinLocalTimeWindow(
  window: LocalTimeWindow | undefined,
  customStartHour?: number,
  customEndHour?: number,
  now: Date = new Date()
): boolean {
  const range = resolveRange(window, customStartHour, customEndHour);
  if (!range) return false;

  const currentHour = now.getHours();
  const { startHour, endHour } = range;

  // Wrap past midnight when endHour <= startHour (e.g., night: 18-6).
  if (endHour <= startHour) {
    return currentHour >= startHour || currentHour < endHour;
  }
  return currentHour >= startHour && currentHour < endHour;
}
